import React, { useEffect, useState } from 'react'
import { Form, Row, Col, Input, Typography, Select, Button, Result, Space, Alert, notification } from 'antd'

import CollectionService from '../../service/CollectionService'
import CountryService from '../../service/CountryService'
import CurrencyService from '../../service/CurrencyService'
import { CollectionPayload, CollectionMethod } from '../../domain/Collection'
import { CountryRetrieve } from '../../domain/Country'
import { ModeContext } from '../../provider/ModeProvider'
import { useNavigate } from 'react-router-dom'

const { Title } = Typography
const { Option } = Select

const currencies: string[] = CurrencyService.allCurrencies
const most_used_currencies: string[] = CurrencyService.mostUsedCurrencies

const channels = [CollectionMethod.LOCAL, CollectionMethod.SWIFT]

const CollectionCreateForm: React.FC = () => {
    const { setMode } = ModeContext()
    const [form] = Form.useForm()
    const [transaction, setTransaction] = useState<string | null>(null)
    const [countries, setCountries] = useState<CountryRetrieve[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [apiNotification, contextNotification] = notification.useNotification()
    const navigate = useNavigate()

    useEffect(() => {
        const countries = CountryService.retrieve()
        setCountries(countries)
    }, [])

    const redirect = (url: string) => {
        navigate(url)
        setMode('')
    }

    const onFinish = () => {
        const values: CollectionPayload = form.getFieldsValue() as CollectionPayload

        setLoading(true)
        CollectionService.simulate(values)
            .then((result) => {
                setTransaction(result.id)
            })
            .catch(() => {
                apiNotification.open({
                    message: 'Simulate Collection Failed',
                    description: 'Something went wrong. Please try again later. If the issue keep happening, please contact Customer Support',
                    placement: 'top',
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const createCollection = () => {
        reset()
        setTransaction(null)
    }

    const reset = () => {
        form.resetFields()
    }

    const prefixSelector = (
        <Form.Item
            name="currency"
            rules={[
                {
                    required: true,
                    message: 'Please input Deposit Currency',
                },
            ]}
            noStyle
        >
            <Select
                placeholder="Currency"
                options={[
                    {
                        label: 'Most Used',
                        options: most_used_currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                    {
                        label: 'Others',
                        options: currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                ]}
            ></Select>
        </Form.Item>
    )

    return (
        <>
            {contextNotification}
            {transaction && (
                <Result
                    status="success"
                    title="Deposit Success"
                    subTitle={`Your Collection ID is ${transaction}`}
                    extra={[
                        <Button type="primary" key="console" onClick={createCollection}>
                            Create Another Collection
                        </Button>,
                        <Button htmlType="submit" onClick={() => redirect(`/collection?id=${transaction}`)}>
                            Update Collection Status
                        </Button>,
                    ]}
                />
            )}

            {!transaction && (
                <Form name="collection-create" layout="vertical" onFinish={onFinish} form={form}>
                    <Row>
                        <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }}>
                            <Title level={5}>Sender Information</Title>
                            <Form.Item
                                label="Name"
                                name="senderName"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Sender Name',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="Account Number"
                                name="senderAccountNumber"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Sender Account Number',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item label="Bank Country" name="senderBankCountry">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Bank Country"
                                    optionFilterProp="children"
                                    filterOption={(input: string, option?: { label: string; value: string }) => {
                                        return (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }}
                                    options={countries.map((country) => ({ label: country.commonName, value: country.iso3166TwoLetterCode }))}
                                />
                            </Form.Item>
                            <Form.Item label="BIC/SWIFT" name="senderBankBIC">
                                <Input />
                            </Form.Item>
                            <Title level={5}>Receiver Information</Title>
                            <Form.Item
                                label="Name"
                                name="receiverName"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Receiver Name',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="Account Number"
                                name="receiverAccountNumber"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Receiver Account Number',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Title level={5}>Deposit Information</Title>
                            <Form.Item
                                name="amount"
                                label="Deposit Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Deposit Amount',
                                    },
                                ]}
                            >
                                <Input addonAfter={prefixSelector} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                label="Payment Channel"
                                name="method"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Payment Channel',
                                    },
                                ]}
                            >
                                <Select placeholder="Select Payment Channel">
                                    {channels.map((channel) => (
                                        <Option key={channel} value={channel}>
                                            {channel.toUpperCase()}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Customer Reference" name="customerReference" rules={[{ message: 'Please input Customer Reference' }]}>
                                <Input />
                            </Form.Item>

                            <Alert
                                message="Collection Status"
                                description="Depending on who you input as sender name, status of created Collection might remain Pending due to our compliance system. You can update it to Completed or Rejected via Update Collection"
                                type="warning"
                            />

                            <Form.Item label=" " colon={false}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Submit
                                    </Button>
                                    <Button htmlType="submit" onClick={reset}>
                                        Reset
                                    </Button>
                                    <Button htmlType="submit" onClick={() => setMode('')}>
                                        Back
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </>
    )
}

export default CollectionCreateForm
